import { v4 as uuid } from 'uuid';

import { uploadFolderImage } from '../api';
import { appendConfirmedEntry, saveSnapshot } from '../lib/storage';
import { FolderUploadMode, ManifestEntry, PreparedFile, UploadState } from '../types';

const RETRY_DELAYS_MS = [0, 1000, 3000, 5000, 10000];
const PARALLEL = 4;

export interface OrchestratorEvents {
    onItemUpdate?: (state: UploadState) => void;
    onProgress?: (confirmed: number, total: number) => void;
}

export class FolderUploadOrchestrator {
    private readonly batchId: string;
    private readonly mode: FolderUploadMode;
    private readonly states: Map<string, UploadState> = new Map();
    private aborted = false;

    constructor(mode: FolderUploadMode, batchId?: string) {
        this.batchId = batchId ?? uuid();
        this.mode = mode;
    }

    public getBatchId(): string {
        return this.batchId;
    }

    public abort(): void {
        this.aborted = true;
    }

    public async run(prepared: PreparedFile[], events: OrchestratorEvents = {}): Promise<ManifestEntry[]> {
        const queue: UploadState[] = [];

        for (const p of prepared) {
            const id = uuid();
            const state: UploadState = {
                upload_id: id,
                file: p.file,
                rowIndex: p.rowIndex,
                sha256: p.sha256,
                status: 'pending',
                attempt: 0,
            };
            this.states.set(id, state);
            queue.push(state);
        }

        saveSnapshot({
            batch_id: this.batchId,
            mode: this.mode,
            created_at: new Date().toISOString(),
            confirmed: [],
        });

        const total = queue.length;
        let confirmedCount = 0;

        const workers = Array.from({ length: Math.min(PARALLEL, queue.length) }, () =>
            this.runWorker(queue, events, () => {
                confirmedCount++;
                events.onProgress?.(confirmedCount, total);
            })
        );

        await Promise.all(workers);

        const manifest: ManifestEntry[] = [];
        this.states.forEach((s) => {
            if (s.status === 'confirmed') {
                manifest.push({
                    upload_id: s.upload_id,
                    row_index: s.rowIndex,
                    filename: s.file.name,
                    sha256: s.sha256,
                    size: s.file.size,
                });
            }
        });
        return manifest;
    }

    private async runWorker(queue: UploadState[], events: OrchestratorEvents, onConfirmed: () => void): Promise<void> {
        while (!this.aborted) {
            const item = queue.shift();
            if (!item) return;

            const ok = await this.uploadWithRetry(item, events);
            if (ok) onConfirmed();
        }
    }

    private async uploadWithRetry(item: UploadState, events: OrchestratorEvents): Promise<boolean> {
        for (let attempt = 0; attempt < RETRY_DELAYS_MS.length; attempt++) {
            if (this.aborted) return false;

            if (RETRY_DELAYS_MS[attempt] > 0) {
                await sleep(RETRY_DELAYS_MS[attempt]);
            }

            this.update(item, { status: 'uploading', attempt: attempt + 1 }, events);

            const res = await uploadFolderImage({
                file: item.file,
                batchId: this.batchId,
                rowIndex: item.rowIndex,
                filename: item.file.name,
                uploadId: item.upload_id,
                sha256: item.sha256,
            });

            if (res.ok) {
                this.update(item, { status: 'confirmed' }, events);
                appendConfirmedEntry(this.batchId, {
                    upload_id: item.upload_id,
                    row_index: item.rowIndex,
                    filename: item.file.name,
                    sha256: item.sha256,
                    size: item.file.size,
                });
                return true;
            }

            // 4xx that aren't 422 (server checksum mismatch) shouldn't be retried.
            if (res.status >= 400 && res.status < 500 && res.status !== 422 && res.status !== 0) {
                this.update(item, { status: 'failed', error: res.message ?? `HTTP ${res.status}` }, events);
                return false;
            }
        }

        this.update(item, { status: 'failed', error: 'retry budget exhausted' }, events);
        return false;
    }

    private update(item: UploadState, patch: Partial<UploadState>, events: OrchestratorEvents): void {
        Object.assign(item, patch);
        events.onItemUpdate?.(item);
    }
}

function sleep(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
}
